import {
  useState,
  useRef,
  useMemo,
  lazy,
  Suspense,
  useEffect,
  memo,
  Dispatch,
  SetStateAction,
  RefObject,
  KeyboardEvent,
} from "react";
import classNames from "classnames";
import styled, { css } from "styled-components/macro";
import TextareaAutosize from "react-autosize-textarea";
import getCaretCoordinates from "textarea-caret";
import {
  BORDER_RADIUS,
  THEME_COLOR,
  WARNING_COLOR,
  ERROR_COLOR,
} from "../vars";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IconButton } from "./Button";
import {
  Plus,
  Brackets,
  Clock,
  ImageFile,
  Microphone,
  Icon,
  Emoji,
  MessageLibrary,
  DotsVertical,
  TrashCan,
  Pencil,
  CalendarPlus,
  VideoOutline,
} from "./Icons";
import Tooltip from "./Tooltip";
import { useFields } from "../hooks/use-fields";
import {
  MenuOverlay,
  MenuButtonItemContainer,
  Menu,
  MenuItemOptions,
  MenuLinkItem,
  MenuButtonItem,
} from "./Menu";
import usePromise from "../hooks/use-promise";
import Overlay from "./Overlay";
import {
  BoxHeader,
  BoxNav,
  BoxNavSearch,
  BoxContent,
  BoxNavLeft,
  BoxNavRight,
  Box,
  BoxNavLink,
} from "./Box";
import FilePicker, { FilePickerTrigger } from "./FilePicker";
import { MediaMessage, SmsMessage } from "./Message";
import { useSkipioApi, touchSkipioApi } from "../api";
import { DatePickerOverlay } from "./DatePicker";
import Highlight from "./Highlight";
import MessageLibrarySidePanel from "./MessageLibrarySidePanel";
import EventSidePanel from "./EventSidePanel";
import usePages from "../hooks/use-pages";
import useScrollTriggers from "../hooks/use-scroll-triggers";
import useDebounce from "../hooks/use-debounce";
import { useToast } from "../hooks/use-toast";
import { MemoryRouter } from "react-router-dom";
import useIsReadonly from "../hooks/use-is-readonly";
import usePlan from "../hooks/use-plan";
import useMoment from "../hooks/use-moment";
import useLocalStorage from "../hooks/use-local-storage";
import ms from "ms";
import { useSkipioQuery } from "../hooks/use-query";
import { MediaMimeType } from "./CampaignEditor/types";
import { fetchUser } from "../api/users";

const Audio = lazy(() => import("./Audio"));

export type TMessage = {
  body?: string;
  deliverAt?: Date | null;
  voicemailId?: string | null;
  imageUrl?: string | null;
  imageType?: MediaMimeType | null;
  mediaUrls?: string[];
  mediaMimeTypes?: MediaMimeType[];
};

interface MessageLibraryMessage extends Omit<TMessage, "body"> {
  id: string;
  name: string;
  body: string; // Required on create, voicemails not allowed
}

type TKeyword = {
  label: string;
  replacement: string;
};

function useInsert(
  ref: RefObject<HTMLTextAreaElement>,
  context: "general" | "event"
) {
  const [showingMode, setShowingMode] = useState<"none" | "emoji" | "keywords">(
    "none"
  );
  const [query, setQuery] = useState("");
  const [activeKeywordIndex, setActiveKeywordIndex] = useState(0);
  const [selectionStart, setSelectionStart] = useState(0);
  const target = ref.current!;
  const [position, setPosition] = useState({ top: 0, left: 0, height: 25 });

  const { data: { default: emojiList } = { default: [] } } = usePromise(
    () => import("../res/emoji.json"),
    []
  );

  const messageKeywords = useSkipioQuery({
    queryKey: ["messageKeywords", { context }],
  }).data as TKeyword[];

  const keywordMap = {
    none: () => [],
    emoji: () =>
      emojiList
        .filter(
          (def) =>
            def.description.toLowerCase().includes(query.toLowerCase()) ||
            def.tags.some((tag) => tag.startsWith(query.toLowerCase()))
        )
        .map((def) => ({
          label: `${def.emoji} ${def.description}`,
          replacement: def.emoji,
        })),
    keywords: () =>
      messageKeywords.filter((keyword) =>
        keyword.label.toLowerCase().includes(query.toLowerCase())
      ),
  };

  const filteredKeywords = keywordMap[showingMode]().slice(0, 100);

  function insert(text: string) {
    window.requestAnimationFrame(() => {
      target.focus();

      if (showingMode !== "none") {
        target.setSelectionRange(
          selectionStart - 1,
          selectionStart + query.length
        );
      }

      // firefox does not understand insertText
      if (!document.execCommand("insertText", false, text)) {
        target.setRangeText(
          text,
          target.selectionStart,
          target.selectionEnd,
          "end"
        );

        target.dispatchEvent(
          new InputEvent("input", {
            data: text,
            inputType: "insertText",
          })
        );
      }

      // @TODO come back to this
      //@ts-expect-error
      target.dispatchEvent(new Event("change", { target }));
    });
  }

  function onKeyUp(e: KeyboardEvent<HTMLTextAreaElement>) {
    const target = e.target as HTMLTextAreaElement;
    if (
      showingMode !== "none" &&
      (filteredKeywords.length === 0 ||
        e.key === "Enter" ||
        e.key === "Escape" ||
        (e.key === "Backspace" && selectionStart > target.selectionStart))
    ) {
      setShowingMode("none");
      setSelectionStart(0);
      setQuery("");
      return;
    }

    if ((e.key === ":" || e.key === "[") && showingMode === "none") {
      if (e.key === ":") {
        setShowingMode("emoji");
      } else {
        setShowingMode("keywords");
      }
      setSelectionStart(target.selectionStart);
      setQuery("");
      setPosition(getCaretCoordinates(ref.current!, target.selectionStart));
      return;
    }

    if (e.key === "Shift" || e.key.startsWith("Arrow")) return;

    const newQuery = target.value.slice(selectionStart, target.selectionStart);

    setQuery(newQuery);
    setActiveKeywordIndex(
      Math.max(0, Math.min(filteredKeywords.length - 1, activeKeywordIndex))
    );
  }

  function pickKeyword(keyword: TKeyword) {
    target.setSelectionRange(selectionStart, selectionStart);
    insert(keyword.replacement);
    setShowingMode("none");
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (showingMode !== "none") {
      if (e.key === "Enter") {
        e.preventDefault();
        pickKeyword(filteredKeywords[activeKeywordIndex]);
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        setActiveKeywordIndex(
          Math.max(
            0,
            Math.min(filteredKeywords.length - 1, activeKeywordIndex - 1)
          )
        );
      } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        setActiveKeywordIndex(
          Math.max(
            0,
            Math.min(filteredKeywords.length - 1, activeKeywordIndex + 1)
          )
        );
      }
    }
  }

  function hideKeywords() {
    setShowingMode("none");
  }

  return {
    insert,
    handlers: { onKeyUp, onKeyDown: onKeyDown },
    isShowingKeywords: showingMode !== "none",
    hideKeywords,
    filteredKeywords,
    activeKeywordIndex,
    position,
    pickKeyword,
  };
}

function Composer<T extends TMessage>({
  placeholder = "Text message body...",
  value,
  onChange,
  enableMessageLibrary = true,
  enableRinglessVoicemail = true,
  enableScheduling = true,
  enableMedia = true,
  enableEventCreation = false,
  allowUnscheduling = true,
  error,
  positionOverlaysBeneath = false,
  contactId,
  context = "general",
  disabled = false,
  showOnlyMessageLength = false,
  optOutInstruction,
  hideVideo = false,
}: {
  value?: T;
  onChange?: Dispatch<SetStateAction<T>>;
  placeholder?: string;
  enableMessageLibrary?: boolean;
  enableRinglessVoicemail?: boolean;
  enableScheduling?: boolean;
  enableMedia?: boolean;
  enableEventCreation?: boolean;
  allowUnscheduling?: boolean;
  positionOverlaysBeneath?: boolean;
  context?: "general" | "event";
  error?: string;
  contactId?: string;
  disabled?: boolean;
  showOnlyMessageLength?: boolean;
  optOutInstruction?: string;
  hideVideo?: boolean;
}) {
  const moment = useMoment();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const ref = useRef<HTMLTextAreaElement>(null);
  const {
    field,
    value: { body, imageUrl, imageType, deliverAt, voicemailId },
    set,
  } = useFields<T>({ value, onChange });

  disabled = useIsReadonly() || disabled;

  const {
    insert: doInsert,
    handlers,
    isShowingKeywords,
    hideKeywords,
    filteredKeywords,
    activeKeywordIndex,
    position,
    pickKeyword,
  } = useInsert(ref, context);

  // @TODO load all
  const voicemailsQuery = useSkipioQuery({
    queryKey: ["recordings", { conversationStatus: "completed" }],
    enabled: enableRinglessVoicemail,
  });
  const voicemails = enableRinglessVoicemail ? voicemailsQuery.data : [];

  const voicemailQuery = useSkipioQuery({
    queryKey: ["recordings", voicemailId],
    enabled: !!voicemailId,
  });
  const voicemail = voicemailId ? voicemailQuery.data : null;

  const insert = (text: string) => {
    doInsert(text);
  };

  const setMessage = (message: MessageLibraryMessage) => {
    set("body", message.body.trim());

    if (message.mediaUrls && message.mediaUrls.length) {
      set("imageUrl", message.mediaUrls[0]);
      set("imageType", message.mediaMimeTypes![0]);
    }
  };

  const activeKeywordRef = useRef<HTMLDivElement>();
  useEffect(() => {
    if (!activeKeywordRef.current) return;
    activeKeywordRef.current!.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }, [activeKeywordIndex]);

  const emojiRegex = /^(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])+$/;

  const isEmoji = !!body && emojiRegex.test(body.trim());

  let bodyLength = body?.length || 0;
  if (optOutInstruction) {
    bodyLength += optOutInstruction.length;
  }

  const mediaButton = (
    mode: "image" | "video",
    hide: () => void,
    trigger: FilePickerTrigger
  ) => (
    <FilePicker
      mode={mode}
      options={mode === "image" ? { maxSize: 0.5 * 1024 * 1024 } : undefined}
      onChange={({ url, mimetype }) => {
        hide();
        set("imageType", mimetype as MediaMimeType);
        set("imageUrl", url);
      }}
      trigger={trigger}
    />
  );

  return (
    <Container error={Boolean(error)}>
      <ContentArea>
        {voicemail ? (
          <Suspense fallback={null}>
            <Audio src={voicemail.audioUrl} key={voicemail.audioUrl} />
          </Suspense>
        ) : (
          <Overlay
            targetRef={ref}
            isShowing={isShowingKeywords}
            setShowing={(t) => !t && hideKeywords()}
            position={{
              bottom: () => {
                const pos =
                  position.top + -(ref.current ? ref.current.scrollTop : 0);

                return pos;
              },
              left: () => position.left,
            }}
            content={() => (
              <KeywordPanel elevated>
                <Menu>
                  {filteredKeywords.map((keyword: TKeyword, index: number) => (
                    <div
                      key={index}
                      style={{ display: "flex", flex: 1 }}
                      ref={(el) => {
                        if (index === activeKeywordIndex)
                          activeKeywordRef.current = el!;
                      }}
                    >
                      <MenuButtonItemContainer
                        onClick={(e) => {
                          e.preventDefault();
                          pickKeyword(keyword);
                        }}
                        style={{ transitionDuration: "0s" }}
                        className={classNames({
                          active: index === activeKeywordIndex,
                        })}
                      >
                        {keyword.label}
                      </MenuButtonItemContainer>
                    </div>
                  ))}
                </Menu>
              </KeywordPanel>
            )}
            trigger={() => (
              <Textarea
                async
                placeholder={placeholder}
                {...handlers}
                value={body || ""}
                onChange={(e) =>
                  set("body", (e.target as HTMLTextAreaElement).value)
                }
                ref={ref}
                disabled={disabled}
                style={{
                  ...(isEmoji
                    ? { fontSize: "40px", lineHeight: "1em" }
                    : undefined),
                  maxHeight: "200px",
                }}
              />
            )}
          />
        )}
        {!!imageUrl &&
          ((imageType || "image").startsWith("image") ? (
            <ContentImage src={imageUrl} />
          ) : (
            <ContentImage src={imageUrl} as="video" controls />
          ))}
      </ContentArea>
      <Toolbar>
        {!disabled ? (
          <div>
            {enableScheduling && (
              <DatePickerOverlay
                bottom={!positionOverlaysBeneath}
                minDate={moment().startOf("day").toDate().getTime()}
                {...field("deliverAt")}
                trigger={({ show, ref }) => (
                  <Tooltip
                    label={"Schedule to send at a specific date and time"}
                  >
                    <ToolbarButton
                      onClick={show}
                      ref={ref}
                      onRemove={
                        allowUnscheduling && deliverAt
                          ? () => set("deliverAt", null)
                          : undefined
                      }
                    >
                      <Clock
                        style={{
                          color: deliverAt ? WARNING_COLOR : undefined,
                        }}
                      />
                    </ToolbarButton>
                  </Tooltip>
                )}
              />
            )}
            <AnimateButtons>
              <Overlay
                border
                position={{
                  ...(!positionOverlaysBeneath
                    ? { bottom: ({ height }) => height + 4 }
                    : { top: ({ height }) => height + 4 }),
                  left: () => 0,
                }}
                trigger={({ ref, show }) => (
                  <Tooltip label="Emoji">
                    <ToolbarButton onClick={show} ref={ref}>
                      <Emoji />
                    </ToolbarButton>
                  </Tooltip>
                )}
                content={({ hide }) => (
                  <EmojiPicker hide={hide} insert={insert} />
                )}
              />
              <MenuOverlay
                bottom={!positionOverlaysBeneath}
                trigger={({ show, ref }) => {
                  return (
                    <Tooltip label="Merge Tags">
                      <ToolbarButton ref={ref} onClick={show}>
                        <Brackets />
                      </ToolbarButton>
                    </Tooltip>
                  );
                }}
                content={({ hide }) => (
                  <MergeTagPicker
                    hide={hide}
                    insert={insert}
                    context={context}
                  />
                )}
              />
              {enableMedia && (
                <MenuOverlay
                  bottom
                  trigger={({ show, ref }) => (
                    <Tooltip label={hideVideo ? "Image" : "Image & Video"}>
                      <ToolbarButton
                        ref={ref}
                        onClick={show}
                        onRemove={
                          imageUrl
                            ? () => {
                                set("imageUrl", "");
                                set("imageType", undefined);
                              }
                            : undefined
                        }
                      >
                        <ImageFile
                          style={{
                            color: imageUrl ? WARNING_COLOR : undefined,
                          }}
                        />
                      </ToolbarButton>
                    </Tooltip>
                  )}
                  content={({ hide }) => (
                    <>
                      {mediaButton("image", hide, ({ show }) => (
                        <MenuButtonItem icon={<ImageFile />} onClick={show}>
                          Image
                        </MenuButtonItem>
                      ))}
                      {!hideVideo &&
                        mediaButton("video", hide, ({ show }) => (
                          <MenuButtonItem
                            icon={<VideoOutline />}
                            onClick={show}
                          >
                            Video
                          </MenuButtonItem>
                        ))}
                    </>
                  )}
                />
              )}
              {enableRinglessVoicemail && (
                <MenuOverlay
                  bottom
                  trigger={({ ref, show }) => (
                    <Tooltip label={"Ringless Voicemail"}>
                      <ToolbarButton
                        ref={ref}
                        onClick={show}
                        onRemove={
                          voicemailId
                            ? () => set("voicemailId", null)
                            : undefined
                        }
                      >
                        <Microphone
                          style={{
                            color: voicemailId ? WARNING_COLOR : undefined,
                          }}
                        />
                      </ToolbarButton>
                    </Tooltip>
                  )}
                  content={({ hide }) => (
                    <>
                      {voicemails.map((voicemail: any, index: number) => (
                        <MenuButtonItem
                          key={index}
                          onClick={() => {
                            hide();
                            set("voicemailId", voicemail.id);
                          }}
                          icon={<Microphone />}
                        >
                          {voicemail.name}
                        </MenuButtonItem>
                      ))}
                    </>
                  )}
                />
              )}
              {enableMessageLibrary && (
                <Overlay
                  position={{
                    ...(!positionOverlaysBeneath
                      ? { bottom: ({ height }) => height + 4 }
                      : { top: ({ height }) => height + 4 }),
                    left: () => 0,
                  }}
                  trigger={({ show, ref }) => {
                    return (
                      <Tooltip label="Message Library">
                        <ToolbarButton onClick={show} ref={ref}>
                          <MessageLibrary />
                        </ToolbarButton>
                      </Tooltip>
                    );
                  }}
                  content={({ hide }) => (
                    <MessageLibraryPicker hide={hide} setMessage={setMessage} />
                  )}
                />
              )}
              {enableEventCreation && (
                <EventSidePanel
                  contactIds={[contactId]}
                  //@ts-expect-error
                  trigger={({ show, ref }) => {
                    return (
                      <Tooltip label="Appointment">
                        <ToolbarButton onClick={show} ref={ref}>
                          <CalendarPlus />
                        </ToolbarButton>
                      </Tooltip>
                    );
                  }}
                />
              )}
            </AnimateButtons>
          </div>
        ) : (
          <div />
        )}
        {(!disabled || showOnlyMessageLength) && (
          <ToolbarRight>
            <span
              style={{
                color:
                  bodyLength > user.maxCharacters ? ERROR_COLOR : undefined,
              }}
            >
              {bodyLength}
            </span>
            {!showOnlyMessageLength && `/${user.maxCharacters}`}
          </ToolbarRight>
        )}
      </Toolbar>
    </Container>
  );
}

function MergeTagPicker({
  insert,
  hide,
  context,
}: {
  insert: (str: string) => void;
  hide: () => void;
  context: "general" | "event";
}) {
  const [query, setQuery] = useState("");

  const keywords = (useSkipioQuery({
    queryKey: ["messageKeywords", { context }],
  }).data as TKeyword[]).filter(
    query
      ? (keyword) => keyword.label.toLowerCase().includes(query.toLowerCase())
      : () => true
  );

  const pick = (keyword: TKeyword) => {
    insert(keyword.replacement);
    hide();
  };

  return (
    <div>
      <BoxHeader>
        <BoxNav>
          <BoxNavSearch
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </BoxNav>
      </BoxHeader>
      <BoxContent style={{ height: "300px", width: "300px" }}>
        <Menu>
          {keywords.map((keyword) => (
            <MenuButtonItemContainer
              key={keyword.replacement}
              onClick={() => pick(keyword)}
            >
              <Highlight query={query}>{keyword.label}</Highlight>
            </MenuButtonItemContainer>
          ))}
        </Menu>
      </BoxContent>
    </div>
  );
}

function EmojiPicker({
  insert,
  hide,
}: {
  insert: (str: string) => void;
  hide: () => void;
}) {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query);

  return (
    <EmojiContainer>
      <BoxHeader>
        <BoxNav>
          <BoxNavSearch
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </BoxNav>
      </BoxHeader>
      <EmojiListController query={debouncedQuery} insert={insert} hide={hide} />
    </EmojiContainer>
  );
}

const EmojiListController = memo(
  function EmojiListController({
    query,
    insert,
    hide,
  }: {
    query: string;
    insert: (str: string) => void;
    hide: () => void;
  }) {
    const [recentlyUsed, setRecentlyUsed] = useLocalStorage<
      Record<string, number>
    >("recently-used-emojis", {});

    useEffect(() => {
      const toRemove: string[] = [];
      for (let [emoji, lastUsed] of Object.entries(recentlyUsed)) {
        if (Date.now() - lastUsed > ms("2w")) {
          toRemove.push(emoji);
        }
      }

      if (toRemove.length) {
        setRecentlyUsed((recentlyUsed) => {
          return Object.fromEntries(
            Object.entries(recentlyUsed).filter(([emoji]) =>
              toRemove.includes(emoji)
            )
          );
        });
      }
    }, []);

    const { data = { default: [] } } = usePromise(
      () => import("../res/emoji.json"),
      []
    );

    type Emoji = {
      emoji: string;
      description: string;
      category: string;
      aliases: string[];
      tags: string[];
    };

    const pick = (emoji: string) => {
      insert(emoji);
      hide();
      setRecentlyUsed((recentlyUsed) => ({
        ...recentlyUsed,
        [emoji]: Date.now(),
      }));
    };

    const groups = useMemo(() => {
      let emojiList: Emoji[] = data.default;

      if (query) {
        emojiList = emojiList.filter(
          (def) =>
            def.description.toLowerCase().includes(query.toLowerCase()) ||
            def.tags.some((tag) => tag.startsWith(query.toLowerCase()))
        );
      }

      const groupBy = (def: Emoji) => def.category;
      return emojiList.reduce((groups, item) => {
        const lastItem =
          groups.length === 0
            ? null
            : groups[groups.length - 1][groups[groups.length - 1].length - 1];
        if (!lastItem) return [[item]];
        else if (groupBy(lastItem) === groupBy(item)) {
          return [
            ...groups.slice(0, groups.length - 1),
            [...groups[groups.length - 1], item],
          ];
        } else return [...groups, [item]];
      }, [] as Emoji[][]);
    }, [query, data]);

    const [page, setPage] = useState(1);
    const shownGroups = query ? groups : groups.slice(0, page);

    const hasMore = page <= groups.length;

    const scrollRef = useScrollTriggers(({ fromBottom }) => {
      if (fromBottom < 100 && hasMore) setPage(page + 1);
    });

    return (
      <EmojiList ref={scrollRef}>
        {!query && Object.keys(recentlyUsed).length > 0 && (
          <EmojiListGroup>
            <EmojiListHeader>Recently Used</EmojiListHeader>
            {Object.entries(recentlyUsed)
              .sort((a, b) => b[1] - a[1])
              .map(([emoji]) => (
                <EmojiButton
                  key={emoji}
                  onClick={() => {
                    pick(emoji);
                  }}
                  data-ignore="true"
                >
                  {emoji}
                </EmojiButton>
              ))}
          </EmojiListGroup>
        )}
        {shownGroups.map((emojiList) => (
          <EmojiListGroup key={emojiList[0].category}>
            <EmojiListHeader>{emojiList[0].category}</EmojiListHeader>
            {emojiList.map((def) => (
              <Tooltip key={def.emoji} label={def.description}>
                <EmojiButton
                  onClick={() => {
                    pick(def.emoji);
                  }}
                  data-ignore="true"
                >
                  {def.emoji}
                </EmojiButton>
              </Tooltip>
            ))}
          </EmojiListGroup>
        ))}
      </EmojiList>
    );
  },
  (p1, p2) => p1.query === p2.query
);

function MessageLibraryPicker({
  setMessage,
  hide,
}: {
  setMessage: (m: MessageLibraryMessage) => void;
  hide: () => void;
}) {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query);
  const [page, setPage] = useState(1);
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState("user");
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const plan = usePlan();

  const { data, loading, hasMore } = usePages<MessageLibraryMessage>(
    skipioApi.sharedSmsMessageTemplates.get,
    {
      query: debouncedQuery,
      ...(mode === "user" ? { userId: user.id } : {}),
    },
    page
  );

  const scrollRef = useScrollTriggers(({ fromBottom }) => {
    if (fromBottom < 100 && !loading && hasMore) setPage(page + 1);
  });

  const showToast = useToast();

  return (
    <Box>
      <BoxHeader stacked={plan.isMultiUser}>
        <BoxNavLeft>
          <BoxNavSearch
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </BoxNavLeft>
        <BoxNavRight style={{ flexGrow: 0, paddingRight: "8px" }}>
          <MessageLibrarySidePanel
            trigger={({ ref, show }) => (
              <IconButton ref={ref} onClick={show}>
                <Plus />
              </IconButton>
            )}
          />
        </BoxNavRight>
      </BoxHeader>
      {plan.isMultiUser && (
        <MemoryRouter initialEntries={["/user"]}>
          <BoxHeader>
            <BoxNav style={{ justifyContent: "center" }}>
              <BoxNavLink to="/user" onClick={() => setMode("user")}>
                My Templates
              </BoxNavLink>
              <BoxNavLink to="/shared" onClick={() => setMode("shared")}>
                Shared
              </BoxNavLink>
            </BoxNav>
          </BoxHeader>
        </MemoryRouter>
      )}
      <BoxContent style={{ height: "350px", width: "350px" }} ref={scrollRef}>
        <Menu>
          {data.map((message) => (
            <MenuButtonItemContainer
              key={message.id}
              onClick={(e) => {
                e.preventDefault();
                setMessage(message);
                hide();
              }}
            >
              <div style={{ padding: "8px", whiteSpace: "unset" }}>
                {message.mediaMimeTypes ? (
                  <>
                    {message.mediaMimeTypes.map((mediaType, index) => (
                      <MediaMessage
                        mediaThumbnail={true}
                        mediaType={mediaType}
                        mediaUrl={message.mediaUrls![index]}
                      />
                    ))}
                    <SmsMessage
                      metadata={
                        <Highlight query={query}>{message.name}</Highlight>
                      }
                    >
                      {message.body}
                    </SmsMessage>
                  </>
                ) : (
                  <SmsMessage
                    metadata={
                      <Highlight query={query}>{message.name}</Highlight>
                    }
                  >
                    {message.body}
                  </SmsMessage>
                )}
              </div>
              {mode === "user" && (
                <MenuItemOptions>
                  <MenuOverlay
                    right
                    trigger={({ show, ref }) => (
                      <IconButton onClick={show} ref={ref}>
                        <DotsVertical />
                      </IconButton>
                    )}
                    content={() => (
                      <>
                        <MessageLibrarySidePanel
                          sharedSmsMessageTemplateId={message.id}
                          trigger={({ ref, show }) => (
                            <MenuButtonItem
                              ref={ref}
                              onClick={show}
                              icon={<Pencil />}
                            >
                              Edit
                            </MenuButtonItem>
                          )}
                        />
                        <MenuButtonItem
                          icon={<TrashCan style={{ color: ERROR_COLOR }} />}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            await skipioApi.sharedSmsMessageTemplates[
                              message.id
                            ].delete();

                            await touchSkipioApi("sharedSmsMessageTemplates");
                            await queryClient.invalidateQueries({
                              queryKey: ["sharedSmsMessageTemplates"],
                            });

                            showToast("Message Template Deleted");
                          }}
                        >
                          Delete
                        </MenuButtonItem>
                      </>
                    )}
                  />
                </MenuItemOptions>
              )}
            </MenuButtonItemContainer>
          ))}
        </Menu>
      </BoxContent>
    </Box>
  );
}

const Container = styled.div<{ error?: boolean }>`
  border-radius: ${BORDER_RADIUS};
  overflow: hidden;
  border: 0.5px solid #dcdcdc;
  background: #fafafa;
  ${(p) =>
    p.error
      ? css`
          border-color: ${ERROR_COLOR};
          border-width: 2px;
          &:focus,
          &:focus-within {
            background: white;
            box-shadow: 0px 0px 0px 2px ${ERROR_COLOR}33;
          }
        `
      : css`
          &:focus-within {
            border-color: ${THEME_COLOR};
            box-shadow: 0px 0px 0px 2px ${THEME_COLOR}33;
            background-color: #fff;
          }
        `}
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: row;
`;

const ContentImage = styled.img`
  border-radius: ${BORDER_RADIUS};
  margin: 10px;
  max-width: 100px;
  max-height: 100px;
  width: auto;
  height: auto;
`;

const Textarea = styled(TextareaAutosize)`
  width: 100%;
  min-height: 80px;
  border: 0;
  resize: none;
  padding: 15px;
  font-size: 14px;
  background: transparent;
  line-height: 1.8em;
`;

const Toolbar = styled.div`
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px;
`;

const ToolbarRight = styled.div`
  padding: 0 10px;
  color: #676767;
  font-size: 12px;
`;

const ToolbarButton = styled(IconButton)`
  transition-duration: 0.2s;
  ${Icon} {
    color: ${THEME_COLOR};
    transition-duration: 0.2s;
  }

  opacity: 1;
  transform: scale(1);
  &.hide {
    opacity: 0;
    transform: scale(0.9);
    pointer-events: none;
  }
`;

const AnimateButtons = styled.div`
  display: inline-block;
`;

const EmojiContainer = styled.div``;

const EmojiButton = styled.div`
  border-radius: 1000px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border: 0;
  background: transparent;
  width: 40px;
  height: 40px;
  font-size: 24px;
  cursor: pointer;
  transition-duration: 0.2s;
  &:hover {
    transform: scale(1.1);
  }
  &:active {
    transform: scale(0.98);
  }
`;

const EmojiList = styled.div`
  display: flex;
  flex-direction: row;
  width: 280px;
  flex-wrap: wrap;
  overflow-x: hidden;
  overflow-y: auto;
  height: 300px;
`;

const EmojiListGroup = styled.div`
  position: sticky;
  width: 100%;
`;

const EmojiListHeader = styled.div`
  padding: 4px 8px;
  padding-top: 12px;
  font-size: 12px;
  color: #676767;
  position: sticky;
  width: 100%;
  border-bottom: 1px solid #eee;
  background: white;
  top: 0;
`;

const KeywordPanel = styled(Box)`
  width: auto;
  height: auto;
  max-height: 200px;
  overflow-y: auto;
  ${MenuLinkItem} {
    white-space: nowrap;
  }
`;

export default Composer;
